import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'rizzui';
import { IoArrowBackOutline } from 'react-icons/io5';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import PageHeader from '../../../layout/shared/page-header';
import { menuRoutes } from '../../../router/menu';
import { CorporationService } from '../../../services/corporation/corporationService';
import AttivaCard from '../../../components/card/AttivaCard';
import { InfoCard, InfoField, InfoGroup, formatInfoDate, formatInfoValue } from '../../contracts/profile/views/info/components/InfoDisplay';
import CorporationForm from '../CorporationForm';

const breadCrumb = [
    { name: 'Administración' },
    { name: 'Empresas fiscales', href: menuRoutes.corporations.path },
    { name: 'Detalle' },
];

type ViewMode = 'summary' | 'edit';

const tabStyles = {
    container: 'max-w-[99vw] sticky z-20 -mx-4 -mt-4 border-b border-muted bg-white px-4 py-0 font-medium text-gray-500 md:-mx-5 md:px-5 lg:-mx-8 lg:mt-0 lg:px-8 xl:-mx-6 xl:px-6 3xl:-mx-[33px] 3xl:px-[33px] 4xl:-mx-10 4xl:px-10 top-[62px] md:top-[71px]',
    list: '-mb-7 flex w-full gap-3 overflow-x-auto scroll-smooth pb-7 md:gap-5 lg:gap-8',
    tab: 'group relative cursor-pointer whitespace-nowrap py-2.5 font-medium text-gray-500',
    active: 'text-primary before:visible before:w-full before:opacity-100',
    inactive: 'before:invisible before:w-0 before:opacity-0 hover:text-gray-900',
};

const CorporationDetailPage = () => {
    const navigate = useNavigate();
    const { userCan } = usePrivilege();
    const { id } = useParams();
    const [viewMode, setViewMode] = useState<ViewMode>('summary');
    const [nodeStatus, setNodeStatus] = useState<boolean>(true);

    const [corporation, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id) return { success: false, data: null, message: 'missing-id' };
        const response = await (new CorporationService()).getCorporationById(id);
        return response.getResponseData();
    }, [id]));

    useEffect(() => {
        if (typeof corporation?.active === 'boolean') {
            setNodeStatus(corporation.active);
        }
    }, [corporation?.active]);

    const relatedCorporations = useMemo(() => {
        const map = new Map<string, { id: string; name: string; relation: string }>();
        const push = (item: any, relation: string) => {
            if (!item?.id || !item?.name || item.id === id) return;
            if (!map.has(item.id)) {
                map.set(item.id, { id: item.id, name: item.name, relation });
            }
        };

        push(corporation?.parentCorporation, 'Empresa padre');
        (corporation?.childrenCorporations || []).forEach((item: any) => push(item?.childCorporation ?? item, 'Empresa hija'));
        (corporation?.providingCorporations || []).forEach((item: any) => push(item, 'Impartidora asociada'));
        (corporation?.organisingCorporations || []).forEach((item: any) => push(item, 'Organizadora asociada'));

        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }, [corporation, id]);

    const canEditCorporation = userCan('edit_corporation', 'corporation');

    return (
        <>
            <PageHeader title="Detalle de empresa fiscal" breadcrumb={breadCrumb}>
                <div className="flex items-center gap-2">
                    <Button variant="flat" size="sm" onClick={() => navigate(-1)}>
                        <IoArrowBackOutline className="me-2 h-4 w-4" />
                        Volver atrás
                    </Button>
                </div>
            </PageHeader>

            <div className={tabStyles.container}>
                <div className="relative flex items-center overflow-hidden">
                    <div className="flex h-[52px] items-start overflow-hidden w-full">
                        <div className={tabStyles.list}>
                            <button
                                type="button"
                                className={`${tabStyles.tab} before:absolute before:bottom-0 before:left-0 before:z-[1] before:h-0.5 before:bg-gray-1000 before:transition-all ${viewMode === 'summary' ? tabStyles.active : tabStyles.inactive}`}
                                onClick={() => setViewMode('summary')}
                            >
                                <span className="inline-flex rounded-md px-2.5 py-1.5 transition-all duration-200 group-hover:bg-gray-100/70">Información</span>
                            </button>
                            {canEditCorporation && (
                                <button
                                    type="button"
                                    className={`${tabStyles.tab} before:absolute before:bottom-0 before:left-0 before:z-[1] before:h-0.5 before:bg-gray-1000 before:transition-all ${viewMode === 'edit' ? tabStyles.active : tabStyles.inactive}`}
                                    onClick={() => setViewMode('edit')}
                                >
                                    <span className="inline-flex rounded-md px-2.5 py-1.5 transition-all duration-200 group-hover:bg-gray-100/70">Editar</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {loading && <Loader height={'70'} />}

            {!loading && (error || !corporation) && (
                <AttivaCard title="" className="text-sm text-gray-600">
                    No se ha podido cargar la empresa fiscal solicitada.
                </AttivaCard>
            )}

            {!loading && corporation && viewMode === 'summary' && (
                <div className="mt-3 flex flex-col gap-5 w-full transition-all duration-300 ease-in-out md:relative pe-2">
                    <InfoCard title="Datos de la Empresa Fiscal" subtitle="Información general, fiscal y de representación legal de la empresa.">
                        <InfoGroup title="Información General y Fiscal" description="Datos administrativos y de facturación de la empresa.">
                            <InfoField label="Nombre" value={formatInfoValue(corporation.name)} className="md:col-span-4" />
                            <InfoField label="CIF/NIF" value={formatInfoValue(corporation.cif)} className="md:col-span-4" />
                            <InfoField label="Función" value={formatInfoValue(corporation.corporationFunction)} className="md:col-span-4" />
                            <InfoField label="IBAN" value={formatInfoValue(corporation.iban)} className="md:col-span-4" />
                            <InfoField label="Código de Factura" value={formatInfoValue(corporation.invoiceCodeFormat)} className="md:col-span-4" />
                            <InfoField label="Secuencia de Factura" value={formatInfoValue(corporation.invoiceSequence)} className="md:col-span-4" />
                            <InfoField label="Código de Factura Atípica" value={formatInfoValue(corporation.nonStandardInvoiceCodeFormat)} className="md:col-span-4" />
                            <InfoField label="Secuencia de Factura Atípica" value={formatInfoValue(corporation.nonStandardInvoiceSequence)} className="md:col-span-4" />
                            <InfoField label="Código de Factura Rectificativa" value={formatInfoValue(corporation.rectificationInvoiceCodeFormat)} className="md:col-span-4" />
                            <InfoField label="Secuencia de Factura Rectificativa" value={formatInfoValue(corporation.rectificationInvoiceSequence)} className="md:col-span-4" />
                            <InfoField label="Identificador Grupo Código" value={formatInfoValue(corporation.groupCodeIdentifier)} className="md:col-span-4" />
                            <InfoField label="Creación" value={formatInfoDate(corporation.createdAt)} className="md:col-span-4" />
                            <InfoField label="Actualización" value={formatInfoDate(corporation.updatedAt)} className="md:col-span-4" />
                        </InfoGroup>

                        <InfoGroup title="Contacto y Localización" description="Datos de ubicación y canales de contacto.">
                            <InfoField label="Domicilio social" value={formatInfoValue(corporation.registeredOffice)} className="md:col-span-6" />
                            <InfoField label="Provincia" value={formatInfoValue(corporation.province)} className="md:col-span-3" />
                            <InfoField label="Ciudad" value={formatInfoValue(corporation.city)} className="md:col-span-3" />
                            <InfoField label="Código postal" value={formatInfoValue(corporation.postalCode)} className="md:col-span-3" />
                            <InfoField label="Email principal" value={formatInfoValue(corporation.email)} className="md:col-span-3" />
                            <InfoField label="Email secundario" value={formatInfoValue(corporation.secondaryEmail)} className="md:col-span-3" />
                            <InfoField label="Teléfono" value={formatInfoValue(corporation.telephone)} className="md:col-span-3" />
                        </InfoGroup>

                        <InfoGroup title="Representante Legal" description="Datos del representante legal y estado de la entidad.">
                            <InfoField label="Nombre representante" value={formatInfoValue(corporation.representativeName)} className="md:col-span-4" />
                            <InfoField label="CIF/NIF representante" value={formatInfoValue(corporation.representativeCifNif)} className="md:col-span-4" />
                            <InfoField label="Email representante" value={formatInfoValue(corporation.representativeEmail)} className="md:col-span-4" />
                            <InfoField label="Teléfono representante" value={formatInfoValue(corporation.representativeTelephone)} className="md:col-span-4" />
                            <InfoField label="Estado" value={corporation.active ? 'Activa' : 'Inactiva'} className="md:col-span-4" />
                        </InfoGroup>

                        <InfoGroup title="Empresas Relacionadas" description="Relaciones jerárquicas y de colaboración entre empresas fiscales.">
                            <InfoField
                                label="Listado de relaciones"
                                className="md:col-span-12"
                                value={relatedCorporations.length > 0 ? (
                                    <div className="flex flex-col gap-1">
                                        {relatedCorporations.map((related) => (
                                            <button
                                                key={related.id}
                                                type="button"
                                                className="text-left text-primary hover:underline"
                                                onClick={() => navigate(`${menuRoutes.corporations.path}/detail/${related.id}`)}
                                            >
                                                {related.name} <span className="text-gray-500">({related.relation})</span>
                                            </button>
                                        ))}
                                    </div>
                                ) : 'N/A'}
                            />
                        </InfoGroup>
                    </InfoCard>
                </div>
            )}

            {!loading && corporation && canEditCorporation && viewMode === 'edit' && (
                <div className="mt-3 flex flex-col gap-5 w-full transition-all duration-300 ease-in-out md:relative pe-2">
                    <AttivaCard title="Edición de Empresa Fiscal" className="border-transparent lg:p-0">
                        <CorporationForm
                            id={id}
                            setNodeId={() => { }}
                            nodeStatus={nodeStatus}
                            setNodeStatus={setNodeStatus}
                            isOpen
                            setIsOpen={(value) => {
                                if (!value) navigate(-1);
                            }}
                            refetch={refetch}
                        />
                    </AttivaCard>
                </div>
            )}
        </>
    );
};

export default CorporationDetailPage;
